import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppStateType } from 'reducers';
import * as TenantServices from 'TenantModule/services';
import {
  ArchiveTenantParams,
  ArchiveTenantSuccessResponse,
  CreateTenantParams,
  CreateTenantSuccessResponse,
  DecommissionTenantParams,
  FetchTenantParams,
  FetchTenantSuccessResponse,
  FetchTenantInfraRequestParams,
  InfraRequestState,
  RequestTenantInfraParams,
  Tenant,
  TenantInfraRequestResponse,
  UnarchiveTenantParams,
  UpdateTenantParams,
  UpdateTenantSuccessResponse,
  VerifyTenantParams,
} from 'TenantModule/types';
import ApiClientManager from 'core/apiClient';

export const fetchTenants = createAsyncThunk<FetchTenantSuccessResponse, FetchTenantParams>(
  'fetchTenants',
  async (
    { meta = {}, ...params }: FetchTenantParams,
    { dispatch: _dispatch, getState, rejectWithValue },
  ) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess, tenantName, realmId } = meta;

    try {
      const response = await TenantServices.fetchTenants(
        params,
        { headers: ApiClientManager.buildUrlHeaders({ tenantName, realmId }) },
        token,
      );
      if (onSuccess) {
        onSuccess({ response: response.data });
      }
      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const createTenant = createAsyncThunk<CreateTenantSuccessResponse, CreateTenantParams>(
  'createTenant',
  async ({ meta = {}, ...params }, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.createTenant(params, {}, token);
      if (onSuccess) {
        onSuccess({ response: response.data });
      }
      return response.data;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

export const updateTenant = createAsyncThunk<UpdateTenantSuccessResponse, UpdateTenantParams>(
  'updateTenant',
  async ({ meta = {}, ...params }: UpdateTenantParams, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.updateTenant(params, {}, token);
      if (onSuccess) {
        onSuccess({ response: response.data });
      }
      return response.data;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

export const archiveTenant = createAsyncThunk<ArchiveTenantSuccessResponse, ArchiveTenantParams>(
  'archiveTenant',
  async ({ meta = {}, ...params }: ArchiveTenantParams, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;
    const { tenant, archiver, force } = params;

    try {
      const response = await TenantServices.archiveTenant(
        {
          tenant,
          ...(force ? { force } : {}),
          meta: {
            name: archiver,
          },
        },
        {},
        token,
      );

      if (onSuccess) {
        onSuccess({ response: response.data });
      }
      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

/* ------------- Tenant lifecycle thunks (RDM-1409 / GLX-1827) ------------- */

export const verifyTenant = createAsyncThunk<Tenant, VerifyTenantParams>(
  'verifyTenant',
  async ({ meta = {}, ...params }, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.verifyTenant(params, {}, token);
      onSuccess?.({ response: response.data });
      return response.data;
    } catch (error) {
      onFailure?.({ error });
      return rejectWithValue(error);
    }
  },
);

export const unarchiveTenant = createAsyncThunk<Tenant, UnarchiveTenantParams>(
  'unarchiveTenant',
  async ({ meta = {}, ...params }, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.unarchiveTenant(params, {}, token);
      onSuccess?.({ response: response.data });
      return response.data;
    } catch (error) {
      onFailure?.({ error });
      return rejectWithValue(error);
    }
  },
);

export const decommissionTenant = createAsyncThunk<Tenant, DecommissionTenantParams>(
  'decommissionTenant',
  async ({ meta = {}, ...params }, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.decommissionTenant(params, {}, token);
      onSuccess?.({ response: response.data });
      return response.data;
    } catch (error) {
      onFailure?.({ error });
      return rejectWithValue(error);
    }
  },
);

/* ------------- Tenant stack PR requests (RDM-2277) ------------- */

export const fetchTenantInfraRequest = createAsyncThunk<
  TenantInfraRequestResponse,
  FetchTenantInfraRequestParams
>('fetchTenantInfraRequest', async ({ meta = {}, tenantName }, { getState, rejectWithValue }) => {
  const {
    authReducer: { user },
  } = getState() as AppStateType;
  const token = user?.auth_token ?? '';
  const { onFailure, onSuccess } = meta;

  try {
    const response = await TenantServices.fetchTenantInfraRequest(tenantName, {}, token);
    onSuccess?.({ response: response.data });
    return response.data;
  } catch (error) {
    onFailure?.({ error });
    return rejectWithValue(error);
  }
});

export const requestTenantInfra = createAsyncThunk<InfraRequestState, RequestTenantInfraParams>(
  'requestTenantInfra',
  async ({ meta = {}, ...params }, { getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const token = user?.auth_token ?? '';
    const { onFailure, onSuccess } = meta;

    try {
      const response = await TenantServices.requestTenantInfra(params, {}, token);
      onSuccess?.({ response: response.data });
      return response.data;
    } catch (error) {
      onFailure?.({ error });
      return rejectWithValue(error);
    }
  },
);
